import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useMediaSyncContextMaybe} from './MediaSyncContextProvider';
import {SyncEventType} from './types';
import {checkForMeaningfulChanges, synchronizeVideo} from './utils/apply';
import {
  createCorrelationContext,
  createSyncCorrelation,
  generateCorrelationId,
} from './utils/correlation';
import {createEventHandlers, createThrottledTimeUpdate} from './utils/publish';
import {retryWithBackoff} from './utils/retry';
import {VideoSyncDebugger} from './utils/videoSyncDebugger';

export type UseVideoSyncOptions = {
  isVideoSychronized?: boolean;
  videoId: string;
};

export type UseVideoSyncReturn = {
  videoRefCallback: (node: HTMLVideoElement | null) => void;
  isSyncEnabled: boolean;
  autoPlay: boolean;
  autoLoop: boolean;
  videoElement: HTMLVideoElement | null;
};

/**
 * Hook for synchronizing video playback across multiple video elements.
 *
 * - Context manages global state and provides stable functions
 * - Hook consumes context functions and manages local state
 * - Hook binds video events to context's publishEvent function
 * - Hook uses context's applySync function to sync video to global state
 */
export const useVideoSync = ({
  isVideoSychronized = false,
  videoId,
}: UseVideoSyncOptions): UseVideoSyncReturn => {
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );

  // Get context - handle case where context is not available
  const context = useMediaSyncContextMaybe();
  const {
    publishEvent,
    isEnabled: isSyncFeatureEnabled,
    autoPlay: autoPlayFromContext,
    autoLoop: autoLoopFromContext,
  } = context;

  const isSyncEnabled: boolean = isVideoSychronized && isSyncFeatureEnabled;
  const autoPlay: boolean = autoPlayFromContext && isSyncEnabled;
  const autoLoop: boolean = autoLoopFromContext && isSyncEnabled;

  // Store the target sync state during sync operations - use ref to avoid useEffect loops
  // When syncing, this contains the state we're syncing TO, allowing us to suppress
  // redundant events without depending on context state in event handlers
  const isApplyingSyncRef = useRef<{
    currentTime: number;
    isPlaying: boolean;
    playbackRate: number;
  } | null>(null);

  const enableAutoPlay = useCallback(
    (node: HTMLVideoElement) => {
      node.onloadeddata = () => {
        publishEvent({
          type: SyncEventType.LEADER,
          data: {
            videoId,
            isPlaying: true,
          },
          videoId,
          timestamp: performance.now(),
        });
      };
    },
    [videoId, publishEvent]
  );

  // Callback ref to capture the video element when it becomes available
  const videoRefCallback = useCallback(
    (node: HTMLVideoElement | null) => {
      setVideoElement(node ?? null);
      if (node && isSyncEnabled && autoPlay) {
        enableAutoPlay(node);
      }
    },
    [autoPlay, isSyncEnabled, enableAutoPlay]
  );

  const videoPlayWithRetryCallback = useCallback(
    (videoElement: HTMLVideoElement) => {
      return retryWithBackoff(async () => videoElement.play(), {
        maxAttempts: 3,
        delayMs: 25, // Shorter delay for video operations
        backoffMultiplier: 2,
        maxDelayMs: 100,
      });
    },
    []
  );

  // Create throttled time update function with play/pause variation
  const throttledTimeUpdate = useMemo(
    () => createThrottledTimeUpdate(publishEvent, isSyncEnabled, videoId, 100),
    [publishEvent, isSyncEnabled, videoId]
  );

  // Enhanced event handlers that integrate with sync system
  const handleVideoEvent = useCallback(
    (eventType: SyncEventType, data?: any) => {
      if (!isSyncEnabled) {
        return;
      }

      // Check if we're currently syncing to a target state
      const targetSyncState = isApplyingSyncRef.current;
      if (targetSyncState) {
        // We're in the middle of a sync operation
        // Check if this event represents the state we're syncing TO
        if (videoElement) {
          const currentTime = data?.currentTime ?? videoElement.currentTime;
          const isPlaying = data?.isPlaying ?? !videoElement.paused;
          const playbackRate = data?.playbackRate ?? videoElement.playbackRate;

          const timeDiff = Math.abs(currentTime - targetSyncState.currentTime);
          const playStateSame = isPlaying === targetSyncState.isPlaying;
          const rateSame =
            Math.abs(playbackRate - targetSyncState.playbackRate) < 0.01;

          // If this event is about the state we're syncing TO, suppress it
          if (
            (eventType === SyncEventType.SEEKED ||
              eventType === SyncEventType.TIME_UPDATE) &&
            timeDiff < 0.025 && // Within 25ms
            playStateSame &&
            rateSame
          ) {
            VideoSyncDebugger.logSync(
              `sync in progress, ignoring event matching target state (timeDiff: ${timeDiff.toFixed(3)}s, target: ${targetSyncState.currentTime.toFixed(3)}s)`,
              videoId,
              eventType,
              {currentTime, isPlaying, playbackRate}
            );
            return;
          }
        }

        // Event doesn't match target state, but we're still syncing
        VideoSyncDebugger.logSync(
          'sync in progress, ignoring event',
          videoId,
          eventType,
          data
        );
        return;
      }

      try {
        // Create correlation context for tracking this event
        const correlationContext = createCorrelationContext(eventType, videoId);

        // Let the context provider handle all filtering logic
        // The hook just publishes all events and lets the context decide what to do
        publishEvent({
          type: eventType,
          data: data,
          timestamp: performance.now(),
          videoId,
          correlationId: correlationContext.correlationId,
        });
      } catch (error) {
        console.warn(`Event handling error for ${videoId}`, error as Error);
      }
    },
    [publishEvent, isSyncEnabled, videoId, videoElement]
  );

  // Create throttled seeking handler to prevent cascades during scrubbing
  // This is especially important when seeking backwards, which fires more events
  const throttledSeeking = useMemo(
    () =>
      _.throttle((video: HTMLVideoElement) => {
        if (video.currentTime === video.duration) {
          return;
        }
        // User is scrubbing - this video should become the leader
        handleVideoEvent(SyncEventType.LEADER, {
          videoId: videoId,
          currentTime: video.currentTime,
          playbackRate: video.playbackRate,
        });
      }, 50),
    [handleVideoEvent, videoId]
  );

  /**
   * Apply context changes to the video element
   *
   * This block ensures our video element stays synchronized with the context.
   * It uses a mutex lock to prevent overlapping sync operations.
   */
  useEffect(() => {
    if (!videoElement || !isSyncEnabled) {
      return;
    }

    // Prevent sync loops by checking if we're already applying sync
    if (isApplyingSyncRef.current) {
      return;
    }

    // Check if we should automatically claim leadership
    // This happens when the leader ends and leaderId becomes null
    if (
      context.leaderId === null &&
      !videoElement.ended &&
      !videoElement.paused
    ) {
      handleVideoEvent(SyncEventType.LEADER);
      return; // Don't sync, just claim leadership
    }

    // Use the tested checkForMeaningfulChanges function
    const safeCurrentTime = isFinite(context.currentTime)
      ? context.currentTime
      : 0;
    const safeDuration = isFinite(videoElement.duration)
      ? videoElement.duration
      : 0;
    const targetTime = Math.min(safeCurrentTime, safeDuration);

    // Create a playback state object for the check
    const playbackState = {
      currentTime: targetTime,
      isPlaying: context.isPlaying,
      playbackRate: context.playbackRate,
      lastUpdate: performance.now(),
    };

    const needsSync = checkForMeaningfulChanges(videoElement, playbackState);
    if (!needsSync) {
      return;
    }
    // Try to get correlation ID and original event type from context if available
    const correlationId = context.correlationId || undefined;
    const originalEventType = context.originalEventType || undefined;
    // Create correlation ID for this sync operation
    const syncCorrelationId = correlationId
      ? createSyncCorrelation(correlationId, videoId)
      : generateCorrelationId();
    synchronizeVideo(
      videoElement,
      videoId,
      SyncEventType.SYNC,
      playbackState,
      isApplyingSyncRef,
      syncCorrelationId,
      originalEventType
    );
  }, [
    videoElement,
    isSyncEnabled,
    context.currentTime,
    context.isPlaying,
    context.playbackRate,
    context.leaderId,
    context.correlationId,
    context.originalEventType,
    videoId,
    handleVideoEvent,
  ]);

  /**
   * Auto Loop handling
   * Handle autoLoop restart when this video becomes the leader or when global state changes
   */
  useEffect(() => {
    if (!videoElement || !isSyncEnabled || !autoLoop) {
      return;
    }

    // Handle autoLoop restart when this video becomes the leader
    if (context.leaderId === videoId) {
      const isAtEnd = videoElement.currentTime === videoElement.duration;
      if (isAtEnd && !isApplyingSyncRef.current) {
        VideoSyncDebugger.logSync(
          `became leader and is at end, restarting for autoLoop, applying still? ${isApplyingSyncRef.current}`,
          videoId,
          SyncEventType.PLAY,
          {
            currentTime: videoElement.currentTime,
            isPlaying: !videoElement.paused || false,
            playbackRate: videoElement.playbackRate || 1,
          }
        );
        // Videos will automatically pause when ending, but sometimes we
        // see this play command getting aborted by that pause command,
        // so we retry it with a delay
        // video has ended, playing the video restarts it from the beginning
        videoPlayWithRetryCallback(videoElement);
      }
    }
  }, [
    videoElement,
    isSyncEnabled,
    autoLoop,
    context.leaderId,
    videoId,
    context.isPlaying,
    context.currentTime,
    videoPlayWithRetryCallback,
  ]);

  /**
   * Auto Play handling
   * We want this to run when the auto play setting changes.
   */
  useEffect(() => {
    if (!videoElement || !isSyncEnabled) {
      return;
    }

    // Only auto-start if the video is not already playing and not ended
    if (autoPlay && videoElement.paused && !videoElement.ended) {
      VideoSyncDebugger.logSync(
        'auto-starting video',
        videoId,
        SyncEventType.PLAY,
        {
          currentTime: videoElement.currentTime,
          isPlaying: false,
          playbackRate: videoElement.playbackRate,
        }
      );
      videoPlayWithRetryCallback(videoElement);
    } else if (!autoPlay && !videoElement.paused) {
      videoElement.pause();
    }
  }, [
    videoElement,
    isSyncEnabled,
    autoPlay,
    videoId,
    videoPlayWithRetryCallback,
  ]);

  // Create event handlers for the video element using the exported utility function
  const createEventHandlersForVideo = useCallback(
    (video: HTMLVideoElement, videoId: string) =>
      createEventHandlers(
        video,
        videoId,
        handleVideoEvent,
        throttledTimeUpdate,
        throttledSeeking
      ),
    [handleVideoEvent, throttledTimeUpdate, throttledSeeking]
  );

  // Set up event listeners when video element becomes available
  useEffect(() => {
    if (!videoElement || !videoId || !isSyncEnabled) {
      return;
    }

    // Create event handlers
    const handlers = createEventHandlersForVideo(videoElement, videoId);

    // Add event listeners
    videoElement.addEventListener('play', handlers.play);
    videoElement.addEventListener('pause', handlers.pause);
    videoElement.addEventListener('seeking', handlers.seeking);
    videoElement.addEventListener('seeked', handlers.seeked);
    videoElement.addEventListener('ratechange', handlers.ratechange);
    videoElement.addEventListener('timeupdate', handlers.timeupdate);
    videoElement.addEventListener('mouseenter', handlers.mouseenter);
    videoElement.addEventListener('ended', handlers.ended);

    // Cleanup function
    return () => {
      videoElement.removeEventListener('play', handlers.play);
      videoElement.removeEventListener('pause', handlers.pause);
      videoElement.removeEventListener('seeking', handlers.seeking);
      videoElement.removeEventListener('seeked', handlers.seeked);
      videoElement.removeEventListener('ratechange', handlers.ratechange);
      videoElement.removeEventListener('timeupdate', handlers.timeupdate);
      videoElement.removeEventListener('mouseenter', handlers.mouseenter);
      videoElement.removeEventListener('ended', handlers.ended);

      // If this video was the leader, trigger leadership election
      if (context.leaderId === videoId) {
        publishEvent({
          type: SyncEventType.ENDED,
          data: {currentTime: videoElement.currentTime},
          videoId,
          timestamp: performance.now(),
        });
      }
    };
  }, [
    videoElement,
    isSyncEnabled,
    createEventHandlersForVideo,
    videoId,
    context.leaderId,
    publishEvent,
  ]);

  return {
    videoRefCallback,
    isSyncEnabled,
    autoPlay,
    autoLoop,
    videoElement,
  };
};
